import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { useToast } from '../../hooks/use-toast';
import api from '../../services/api';
import {
  MessageCircle,
  Send,
  Settings,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Loader2,
  Calendar,
  Bell,
  Play,
  AlertTriangle,
  ExternalLink,
  Smartphone
} from 'lucide-react';

const AdminNotificaciones = () => {
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [triggeringJob, setTriggeringJob] = useState(null);
  const { toast } = useToast();

  // Form states
  const [directMessage, setDirectMessage] = useState({ phone: '', message: '' });
  const [templateMessage, setTemplateMessage] = useState({
    template: 'webinar_reminder',
    phone: '',
    data: {}
  });
  const [bulkNotification, setBulkNotification] = useState({
    template: 'membership_expiring',
    target: 'expiring'
  });

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      const [whatsapp, scheduler] = await Promise.all([
        api.get('/notifications/whatsapp/status'),
        api.get('/scheduler/status')
      ]);
      setWhatsappStatus(whatsapp.data);
      setSchedulerStatus(scheduler.data);
    } catch (error) {
      console.error('Error fetching statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendDirectMessage = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const response = await api.post('/notifications/whatsapp/send-direct', directMessage);
      if (response.data.simulated) {
        toast({
          title: "Mensaje simulado",
          description: "WhatsApp no está configurado. El mensaje se simularía así:",
        });
      } else {
        toast({
          title: "Mensaje enviado",
          description: `Enviado a ${directMessage.phone}`
        });
      }
      setDirectMessage({ phone: '', message: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo enviar el mensaje",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const triggerJob = async (jobName, dryRun = false) => {
    setTriggeringJob(jobName);
    try {
      const response = await api.post('/scheduler/trigger', {
        job_name: jobName,
        dry_run: dryRun
      });
      toast({
        title: dryRun ? "Simulación iniciada" : "Job iniciado",
        description: response.data.message
      });
      setTimeout(fetchStatuses, 2000);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo iniciar el job",
        variant: "destructive"
      });
    } finally {
      setTriggeringJob(null);
    }
  };

  const getJobStatusColor = (status) => {
    switch (status) {
      case 'running': return 'bg-blue-100 text-blue-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'error': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="admin-notifications-title">
            Centro de Notificaciones
          </h1>
          <p className="text-gray-500 mt-1">
            WhatsApp, Email y tareas programadas
          </p>
        </div>
        <Button variant="outline" onClick={fetchStatuses}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* WhatsApp Status */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${whatsappStatus?.configured ? 'bg-green-100' : 'bg-yellow-100'}`}>
                <MessageCircle className={`h-5 w-5 ${whatsappStatus?.configured ? 'text-green-600' : 'text-yellow-600'}`} />
              </div>
              <div>
                <CardTitle className="text-lg">WhatsApp Business API</CardTitle>
                <CardDescription>
                  {whatsappStatus?.configured 
                    ? 'Configurado y listo para enviar mensajes'
                    : 'No configurado - los mensajes se simularán'}
                </CardDescription>
              </div>
            </div>
            <Badge className={whatsappStatus?.configured ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
              {whatsappStatus?.configured ? 'Conectado' : 'No Configurado'}
            </Badge>
          </div>
        </CardHeader>
        {!whatsappStatus?.configured && whatsappStatus?.setup_instructions && (
          <CardContent className="pt-0">
            <div className="bg-gray-50 rounded-lg p-4 mt-2">
              <h4 className="font-medium text-gray-900 mb-2">Instrucciones de configuración:</h4>
              <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                {Object.entries(whatsappStatus.setup_instructions).map(([key, value]) => (
                  key !== 'docs' && <li key={key}>{value}</li>
                ))}
              </ol>
              {whatsappStatus.setup_instructions.docs && (
                <a 
                  href={whatsappStatus.setup_instructions.docs}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline text-sm flex items-center mt-3"
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  Ver documentación oficial
                </a>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="scheduler" className="space-y-4">
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="scheduler">
            <Clock className="h-4 w-4 mr-2" />
            Programador
          </TabsTrigger>
          <TabsTrigger value="whatsapp">
            <Smartphone className="h-4 w-4 mr-2" />
            WhatsApp
          </TabsTrigger>
          <TabsTrigger value="templates">
            <MessageCircle className="h-4 w-4 mr-2" />
            Plantillas
          </TabsTrigger>
        </TabsList>

        {/* Scheduler Tab */}
        <TabsContent value="scheduler">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {schedulerStatus?.jobs && Object.entries(schedulerStatus.jobs).map(([jobName, job]) => (
              <Card key={jobName}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base capitalize">
                      {jobName.replace(/_/g, ' ')}
                    </CardTitle>
                    <Badge className={getJobStatusColor(job.status)}>
                      {job.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {job.last_run && (
                    <p className="text-xs text-gray-500">
                      Última ejecución: {new Date(job.last_run).toLocaleString('es-MX')}
                    </p>
                  )}
                  {job.last_result && (
                    <div className="text-xs bg-gray-50 p-2 rounded">
                      <p>Procesados: {job.last_result.processed || 0}</p>
                      <p>Enviados: {job.last_result.sent || 0}</p>
                      {job.last_result.errors > 0 && (
                        <p className="text-red-600">Errores: {job.last_result.errors}</p>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => triggerJob(jobName, true)}
                      disabled={triggeringJob === jobName}
                      className="flex-1"
                    >
                      {triggeringJob === jobName ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <>
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Simular
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => triggerJob(jobName, false)}
                      disabled={triggeringJob === jobName}
                      className="flex-1 bg-gray-900 hover:bg-gray-800"
                    >
                      {triggeringJob === jobName ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <>
                          <Play className="h-3 w-3 mr-1" />
                          Ejecutar
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-lg">Ejecutar Todos los Jobs</CardTitle>
              <CardDescription>
                Envía recordatorios de webinars y membresías a todos los usuarios correspondientes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      await api.post('/scheduler/run-all?dry_run=true');
                      toast({ title: "Simulación iniciada", description: "Todos los jobs en modo simulación" });
                    } catch (e) {
                      toast({ title: "Error", variant: "destructive" });
                    }
                  }}
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Simular Todos
                </Button>
                <Button
                  className="bg-gray-900 hover:bg-gray-800"
                  onClick={async () => {
                    try {
                      await api.post('/scheduler/run-all?dry_run=false');
                      toast({ title: "Jobs iniciados", description: "Todos los jobs están ejecutándose" });
                    } catch (e) {
                      toast({ title: "Error", variant: "destructive" });
                    }
                  }}
                >
                  <Play className="h-4 w-4 mr-2" />
                  Ejecutar Todos
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WhatsApp Tab */}
        <TabsContent value="whatsapp">
          <Card>
            <CardHeader>
              <CardTitle>Enviar Mensaje Directo</CardTitle>
              <CardDescription>
                Envía un mensaje de WhatsApp personalizado a un número específico
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={sendDirectMessage} className="space-y-4">
                <div>
                  <Label>Número de teléfono</Label>
                  <Input
                    type="tel"
                    placeholder="+52 55 1234 5678"
                    value={directMessage.phone}
                    onChange={(e) => setDirectMessage({...directMessage, phone: e.target.value})}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Incluye código de país. Ej: 5512345678
                  </p>
                </div>
                <div>
                  <Label>Mensaje</Label>
                  <Textarea
                    placeholder="Escribe tu mensaje aquí..."
                    value={directMessage.message}
                    onChange={(e) => setDirectMessage({...directMessage, message: e.target.value})}
                    rows={4}
                    required
                  />
                </div>
                <Button type="submit" disabled={sending} className="bg-green-600 hover:bg-green-700">
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Enviar WhatsApp
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle>Plantillas de Notificación</CardTitle>
              <CardDescription>
                Plantillas predefinidas para diferentes tipos de notificaciones
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { id: 'webinar_reminder', name: 'Recordatorio de Webinar', icon: Calendar },
                  { id: 'membership_expiring', name: 'Membresía por Expirar', icon: Bell },
                  { id: 'payment_confirmation', name: 'Confirmación de Pago', icon: CheckCircle },
                  { id: 'webinar_access', name: 'Acceso a Webinar', icon: MessageCircle }
                ].map((template) => (
                  <div 
                    key={template.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white rounded-lg shadow-sm">
                        <template.icon className="h-5 w-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{template.name}</p>
                        <p className="text-sm text-gray-500">Template ID: {template.id}</p>
                      </div>
                    </div>
                    <Badge variant="outline">ES / EN</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Server Info */}
      <Card className="bg-gray-50">
        <CardContent className="py-4">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Hora del servidor: {new Date(schedulerStatus?.server_time).toLocaleString('es-MX')}</span>
            <span className="flex items-center gap-2">
              SendGrid: 
              <Badge className={schedulerStatus?.sendgrid_configured ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                {schedulerStatus?.sendgrid_configured ? 'Configurado' : 'No Configurado'}
              </Badge>
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminNotificaciones;
